import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloudIcon,
  CopyIcon,
  LinkIcon,
  MailIcon,
  PhoneIcon,
  RefreshCwIcon,
  PlusIcon,
  BellIcon,
  BellOffIcon,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { readNotificationPreferences, saveSubjectNotificationOverride, subjectKey } from '@/lib/notifications';
import { toast } from 'sonner';
import {
  Avatar,
  BackLink,
  Button,
  EmptyState,
  ErrorNotice,
  FactList,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
  Tabs,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { eventStageTone } from '@/lib/event-status';
import {
  formatEventDate,
  formatEventTime,
  formatViewerDateTime,
  hasEventZone,
  viewerSharesEventClock,
} from '@/lib/event-time';
import { AttachmentsTab, NotesTab, PaymentTab, RecapTab, ShipmentsTab } from './event-tabs';
import { CharityPanel } from './event-charity';
import { ChecklistTab } from './event-checklists-tab';
import { AssistantsPanel } from './event-assistants';
import { RecordingsPanel } from './event-recordings';
import { LocalContacts } from './event-contacts';
import { SummaryTab } from './event-summary';
import { LogisticsTab } from './event-logistics';
import { MaterialsTab } from './event-packing-list';
import { CsrTab } from './event-csr-tab';
import { VenueRoomsTab } from './event-client-portal';
import { ClientPortalTab } from './event-client-portal-tab';
import { MessagesTab } from './event-messages-tab';
import { ContractFamilyPanel } from './event-family';
import { ContractsTab } from './event-contracts-tab';
import { EventCostsTab } from '@/components/event-pnl';
import { ClientSection } from '@/components/ClientSection';
import { APP_FEATURE, useAppFeatures, type AppFeatureKey, type IsFeatureOn } from '@/lib/app-features';
import { AddEventPersonDialog, ROSTER_KIND, ROSTER_KINDS, type RosterKind } from '@/components/AddEventPersonDialog';
import { BookingLinkDialog } from '@/components/BookingLinkDialog';
import { PaymentLinkDrawer } from '@/components/PaymentLinkDrawer';
import {
  addEventUsers,
  avatarSrc,
  hasPermission,
  PERMISSION_MANAGE_PAYMENTS,
  PERMISSION_VIEW_ACCOUNTING,
  listEventUsers,
  personName,
  readEvent,
  readEventCsr,
  readMyIdentity,
  removeEventUsers,
  syncEventFromSalesforce,
  type EventContact,
  type EventDetail,
  type EventPerson,
} from '@/lib/events';

/** The sections of one event. */
const EVENT_TABS = ['overview', 'checklist', 'logistics', 'venue', 'csr', 'materials', 'people', 'summary', 'notes', 'messages', 'shipments', 'attachments', 'recap', 'payment', 'contracts', 'costs', 'portal'] as const;
type EventTab = (typeof EVENT_TABS)[number];

/** Tabs that belong to a switchable feature (Settings → Features). */
const TAB_FEATURE: Partial<Record<EventTab, AppFeatureKey>> = {
  materials: APP_FEATURE.materials,
  messages: APP_FEATURE.messages,
};

/** Everything on: what the tab list assumes when no switches are given. */
const ALL_FEATURES_ON: IsFeatureOn = () => true;

/**
 * The event's tab bar.
 *
 * Extracted from the page so the one rule that has bitten us is testable
 * without rendering it: **Request payment is not part of the client-payments
 * rollout**. A facilitator's request for payment is their invoice to BCE — a
 * Formstack form that has always been available on every event — while
 * `myEventsContracting` governs whether *client* contracting and card payments
 * run through My Events. Gating the two together hid the invoice form from
 * facilitators on every event where the rollout was off.
 *
 * @param options - The attendee count, and whether the client-contracting
 * rollout is on for this event.
 * @returns The tabs, in display order.
 */
export function eventTabItems(options: {
  readonly peopleCount?: number | undefined;
  readonly myEventsContracting: boolean;
  /** A CSR event: Salesforce filed a charity on it, or a CSR plan already exists. */
  readonly csr?: boolean;
  /** The client is on the portal: their venue plan can be managed from BCE's side. */
  readonly clientPortal?: boolean;
  /** Which switchable features are on; omitted, everything is. */
  readonly isFeatureOn?: IsFeatureOn;
  /**
   * The viewer holds view_accounting and the event has a Salesforce
   * opportunity: the Costs tab (its statement and expense lines) shows.
   * Everyone else never sees the tab.
   */
  readonly costs?: boolean;
}): readonly { readonly key: EventTab; readonly label: string; readonly count?: number }[] {
  const isFeatureOn = options.isFeatureOn ?? ALL_FEATURES_ON;
  const items: readonly { readonly key: EventTab; readonly label: string; readonly count?: number }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'checklist', label: 'Checklist' },
    { key: 'logistics', label: 'Logistics' },
    ...(options.clientPortal ? [{ key: 'venue' as const, label: 'Venue & rooms' }] : []),
    ...(options.csr ? [{ key: 'csr' as const, label: 'Charity' }] : []),
    { key: 'materials', label: 'Materials' },
    ...(options.peopleCount === undefined
      ? [{ key: 'people' as const, label: 'Team & attendees' }]
      : [{ key: 'people' as const, label: 'Team & attendees', count: options.peopleCount }]),
    // Summary is hidden for now (Thomas, 2026-09-07); the tab's code and routes stay.
    { key: 'notes', label: 'Notes' },
    { key: 'messages', label: 'Messages' },
    { key: 'shipments', label: 'Shipments' },
    { key: 'attachments', label: 'Attachments' },
    { key: 'recap', label: 'Recap' },
    { key: 'payment', label: 'Request payment' },
    ...(options.myEventsContracting
      ? [{ key: 'contracts' as const, label: 'Contracts' }]
      : []),
    ...(options.costs ? [{ key: 'costs' as const, label: 'Costs' }] : []),
    // Last: what the client's portal shows is a setting, not a part of the day.
    { key: 'portal', label: 'Client portal' },
  ];
  return items.filter((item) => {
    const feature = TAB_FEATURE[item.key];
    return feature === undefined || isFeatureOn(feature);
  });
}


/** Narrows a ?tab= value to a real tab; anything else lands on Overview. */
function asEventTab(value: string | null): EventTab {
  return (EVENT_TABS as readonly string[]).includes(value ?? '') ? (value as EventTab) : 'overview';
}

/**
 * One event: the operational home page for everyone delivering it.
 *
 * The old internal client presented an event as a form. This screen presents
 * it as a briefing — the facts a facilitator needs at a glance, then the team,
 * notes and logistics — because the person opening it is more often *doing*
 * the event than editing it.
 *
 * @returns The event detail screen.
 */
export function EventDetailPage(): React.ReactElement {
  const { eventId } = useParams<{ eventId: string }>();
  const [params, setParams] = useSearchParams();
  const isFeatureOn = useAppFeatures();
  const requested = asEventTab(params.get('tab'));
  // A link to a tab whose feature is switched off lands on the overview.
  const requestedFeature = TAB_FEATURE[requested];
  const featureTab = requestedFeature === undefined || isFeatureOn(requestedFeature) ? requested : 'overview';

  const event = useQuery({
    queryKey: ['event', eventId],
    queryFn: ({ signal }) => readEvent(eventId ?? '', signal),
    enabled: eventId !== undefined,
  });

  // Whether the event has a CSR side decides whether the tab shows at all.
  const csr = useQuery({
    queryKey: ['event-csr', eventId],
    queryFn: ({ signal }) => readEventCsr(eventId ?? '', signal),
    enabled: eventId !== undefined,
  });

  const identity = useQuery({
    queryKey: ['my-identity'],
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });

  const people = useQuery({
    queryKey: ['event-users', eventId],
    queryFn: ({ signal }) => listEventUsers(eventId ?? '', signal),
    enabled: eventId !== undefined,
  });

  if (event.isPending) return <LoadingState label="Loading event…" />;
  if (event.isError) {
    return <ErrorNotice title="Event unavailable" message={errorMessage(event.error)} />;
  }

  const detail = event.data;
  const when = formatEventDate(detail) ?? null;
  const clientPortal = csr.data?.organization != null;
  // Costs carry facilitator pay: view_accounting only, and only with an opportunity to read.
  const opportunityId = detail.opportunityId ?? '';
  const showCosts = hasPermission(identity.data, PERMISSION_VIEW_ACCOUNTING) && opportunityId !== '';
  const tab = featureTab === 'costs' && !showCosts ? 'overview' : featureTab;

  return (
    <section className="animate-portal-rise">
      <BackLink to="/events">All events</BackLink>

      <div className="mt-3">
        <PageHeading guide="event-heading"
          eyebrow={
            [detail.contractNumber, detail.programDisplayName || detail.programName]
              .filter(Boolean)
              .join(' · ') || 'EVENT'
          }
          size="compact"
          title={detail.account?.name || 'Untitled event'}
          {...(when ? { subtitle: when } : {})}
          action={
            <div className="flex flex-col items-end gap-2">
              {detail.stageName ? (
                <Pill tone={eventStageTone(detail.stageName)}>{detail.stageName}</Pill>
              ) : null}
              <EventActions detail={detail} />
            </div>
          }
        />
      </div>

      <div data-guide="event-tabs"><Tabs<EventTab>
        label="Event sections"
        items={[
          ...eventTabItems({
            peopleCount: people.data?.length,
            myEventsContracting: detail?.myEventsContracting === true,
            csr: (detail?.charity?.length ?? 0) > 0 || csr.data?.plan != null,
            clientPortal,
            isFeatureOn,
            costs: showCosts,
          }),
        ]}
        active={tab}
        onChange={(next) =>
          setParams(
            (previous) => {
              const nextParams = new URLSearchParams(previous);
              nextParams.set('tab', next);
              return nextParams;
            },
            { replace: true },
          )
        }
      /></div>

      {tab === 'overview' ? <OverviewTab detail={detail} /> : null}
      {tab === 'checklist' && eventId !== undefined ? (
        <ChecklistTab eventId={eventId} clientPortal={clientPortal} />
      ) : null}
      {tab === 'logistics' && eventId !== undefined ? <LogisticsTab eventId={eventId} /> : null}
      {tab === 'materials' && eventId !== undefined ? <MaterialsTab eventId={eventId} /> : null}
      {tab === 'venue' && eventId !== undefined ? <VenueRoomsTab eventId={eventId} /> : null}
      {tab === 'csr' && eventId !== undefined ? <CsrTab eventId={eventId} /> : null}
      {tab === 'people' && eventId !== undefined ? (
        <PeopleTab
          eventId={eventId}
          people={people.data ?? []}
          loading={people.isPending}
          contacts={detail.contacts ?? []}
          contactEmails={detail.contactEmails ?? []}
        />
      ) : null}
      {tab === 'summary' && eventId !== undefined ? <SummaryTab eventId={eventId} /> : null}
      {tab === 'notes' && eventId !== undefined ? <NotesTab eventId={eventId} /> : null}
      {tab === 'messages' && eventId !== undefined ? (
        <MessagesTab eventId={eventId} clientPortal={clientPortal} />
      ) : null}
      {tab === 'shipments' && eventId !== undefined ? (
        <ShipmentsTab eventId={eventId} clientPortal={clientPortal} />
      ) : null}
      {tab === 'attachments' && eventId !== undefined ? <AttachmentsTab eventId={eventId} /> : null}
      {tab === 'recap' && eventId !== undefined ? (
        <RecapTab eventId={eventId} offersClientFeedback={detail.offersClientFeedback === true} />
      ) : null}
      {tab === 'payment' && eventId !== undefined ? (
        <PaymentTab eventId={eventId} detail={detail} />
      ) : null}
      {tab === 'contracts' && eventId !== undefined ? <ContractsTab subject={{ kind: 'event', id: eventId }} /> : null}
      {tab === 'costs' && showCosts ? <EventCostsTab opportunityId={opportunityId} /> : null}
      {tab === 'portal' && eventId !== undefined ? <ClientPortalTab eventId={eventId} /> : null}
    </section>
  );
}

/** The clipboard text for an event — the legacy copy format, verbatim. */
function eventClipboardText(detail: EventDetail): string {
  const lines: string[] = [];
  lines.push(`Contract Number: ${detail.contractNumber ?? ''}`);
  const date = formatEventDate(detail) ?? '';
  // The zone rides on the time, as legacy did — a pasted event that says
  // "3:00pm" without saying whose 3pm is how people end up in the wrong city.
  const time = formatEventTime(detail);
  lines.push(`Event Date: ${date}${time ? ` from ${time}` : ''}`);
  lines.push(`Program Name: ${detail.programDisplayName ?? detail.programName ?? ''}`);
  lines.push(`Participants: ${detail.participants ?? ''}`);
  if (detail.contact) {
    lines.push(`Contact: ${detail.contact.name ?? ''}`);
    if (detail.contact.phone) lines.push(`\tContact Phone: ${detail.contact.phone}`);
    if (detail.contact.cell) lines.push(`\tContact Mobile: ${detail.contact.cell}`);
    if (detail.contact.email) lines.push(`\tContact Email: ${detail.contact.email}`);
  }
  if (detail.startVenue?.name) {
    const address = [
      detail.startVenue.street,
      detail.startVenue.city,
      detail.startVenue.state,
      detail.startVenue.postalCode,
    ]
      .filter(Boolean)
      .join(', ');
    lines.push(`Venue: ${detail.startVenue.name}${address ? ` - ${address}` : ''}`);
  }
  return lines.join('\r\n');
}

/**
 * One header action: text label on large screens, icon-only on small, always
 * with a tooltip. Renders as a link or a button depending on the props.
 */
function HeaderAction({
  icon: Icon,
  label,
  tooltip,
  onClick,
  to,
  href,
  disabled,
  spinning,
}: {
  readonly icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  readonly label: string;
  readonly tooltip: string;
  readonly onClick?: () => void;
  readonly to?: string;
  readonly href?: string;
  readonly disabled?: boolean;
  readonly spinning?: boolean;
}): React.ReactElement {
  const className = cn(
    'flex h-8 items-center gap-1.5 rounded-portal-button border border-portal-line bg-white px-2.5 text-portal-micro font-bold transition-colors',
    disabled
      ? 'cursor-not-allowed text-portal-muted/50'
      : 'text-portal-ink hover:border-brand hover:text-brand',
  );
  const body = (
    <>
      <Icon aria-hidden className={cn('size-3.5 shrink-0', spinning && 'animate-spin')} />
      <span className="hidden lg:inline">{label}</span>
    </>
  );
  const control =
    to && !disabled ? (
      <Link to={to} className={className} aria-label={label}>
        {body}
      </Link>
    ) : href && !disabled ? (
      <a href={href} target="_blank" rel="noreferrer noopener" className={className} aria-label={label}>
        {body}
      </a>
    ) : (
      <button type="button" onClick={onClick} disabled={disabled} className={className} aria-label={label}>
        {body}
      </button>
    );
  return (
    <Tooltip>
      <TooltipTrigger asChild>{control}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/** The event header's action row: copy, Salesforce, sync, previous/next. */
function EventActions({ detail }: { readonly detail: EventDetail }): React.ReactElement {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  // Salesforce is an internal system: contractors get neither the link nor
  // the sync action (the API refuses the sync anyway; hiding avoids dead
  // controls and keeps Salesforce out of their sight entirely).
  const identity = useQuery({
    queryKey: ['my-identity'],
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });
  const isInternal = ['internal', 'admin', 'superadmin'].includes(identity.data?.role ?? '');
  // Payment links are for BCEcloud-contracted events, and for staff who manage payments.
  const canSendPaymentLink = detail.myEventsContracting === true && hasPermission(identity.data, PERMISSION_MANAGE_PAYMENTS);
  const [paymentLinkOpen, setPaymentLinkOpen] = useState(false);
  const sync = useMutation({
    mutationFn: () => syncEventFromSalesforce(detail._id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['event', detail._id] });
    },
  });

  return (
    <TooltipProvider delayDuration={100}>
      <div data-guide="event-actions" className="flex items-center gap-1.5">
        <HeaderAction
          icon={copied ? CheckIcon : CopyIcon}
          label={copied ? 'Copied' : 'Copy'}
          tooltip="Copy the event details to the clipboard"
          onClick={() => {
            navigator.clipboard.writeText(eventClipboardText(detail)).catch(() => {});
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
        />
        {isInternal && detail.opportunityId ? (
          <HeaderAction
            icon={CloudIcon}
            label="Salesforce"
            tooltip="View this opportunity in Salesforce"
            href={`https://bestcorporateevents.lightning.force.com/${detail.opportunityId}`}
          />
        ) : null}
        <EventFollowAction eventId={detail._id} />
        {canSendPaymentLink ? (
          <HeaderAction
            icon={LinkIcon}
            label="Pay link"
            tooltip="Copy or email a link the client can pay this event's balance with"
            onClick={() => setPaymentLinkOpen(true)}
          />
        ) : null}
        {isInternal ? (
          <HeaderAction
            icon={RefreshCwIcon}
            label={sync.isPending ? 'Syncing…' : 'Sync'}
            tooltip="Re-import this event's data from Salesforce"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
            spinning={sync.isPending}
          />
        ) : null}
        <span className="mx-0.5 h-5 w-px bg-portal-line" aria-hidden />
        <HeaderAction
          icon={ChevronLeftIcon}
          label="Previous"
          tooltip="Previous event by date"
          to={detail.previousEventId ? `/events/${detail.previousEventId}` : undefined}
          disabled={!detail.previousEventId}
        />
        <HeaderAction
          icon={ChevronRightIcon}
          label="Next"
          tooltip="Next event by date"
          to={detail.nextEventId ? `/events/${detail.nextEventId}` : undefined}
          disabled={!detail.nextEventId}
        />
      </div>
      {paymentLinkOpen ? (
        <PaymentLinkDrawer
          open
          eventId={detail._id}
          title={[detail.contractNumber ? `#${detail.contractNumber}` : null, detail.account?.name].filter(Boolean).join(' · ')}
          onClose={() => setPaymentLinkOpen(false)}
        />
      ) : null}
    </TooltipProvider>
  );
}

/**
 * Follow or mute this event's notifications for the viewer. A muted event
 * sends nothing on any channel; the global preferences stay as they are.
 */
function EventFollowAction({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const preferences = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: ({ signal }) => readNotificationPreferences(signal),
    staleTime: 60_000,
  });
  const muted = preferences.data?.overrides[subjectKey('event', eventId)]?.muted === true;
  const toggle = useMutation({
    mutationFn: () => saveSubjectNotificationOverride('event', eventId, muted ? null : { muted: true }),
    onSuccess: async () => {
      toast.success(muted ? 'Following this event again' : 'Muted this event');
      await queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <HeaderAction
      icon={muted ? BellOffIcon : BellIcon}
      label={muted ? 'Muted' : 'Following'}
      tooltip={muted ? 'Notifications for this event are muted — click to follow again' : 'You get this event’s notifications — click to mute'}
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
    />
  );
}

/**
 * The facts a facilitator checks before setting off.
 *
 * @param props - The event.
 * @returns The overview panel.
 */
function OverviewTab({ detail }: { readonly detail: EventDetail }): React.ReactElement {
  // Hand-added contacts are the program manager's to keep; contractors read.
  const identity = useQuery({
    queryKey: ['my-identity'],
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });
  const internalViewer = ['internal', 'admin', 'superadmin'].includes(identity.data?.role ?? '');
  const role = identity.data?.role ?? '';
  const canEditContacts = ['internal', 'admin', 'superadmin'].includes(role);
  const isAdmin = ['admin', 'superadmin'].includes(role);
  const [addingContact, setAddingContact] = useState(false);
  const [linking, setLinking] = useState(false);
  const booking = detail.bookingRequest ?? null;
  const participants =
    detail.participants === undefined
      ? undefined
      : `${detail.participants}${detail.afterParticipants ? ` (${detail.afterParticipants})` : ''}`;
  // Three separate facts, as the legacy tool had them: the day, the clock at
  // the venue, and the same moment on the reader's own clock. The last one is
  // what keeps a facilitator in another zone from packing for the wrong hour.
  const time = formatEventTime(detail, '–');
  const local = formatViewerDateTime(detail);
  const sameClock = viewerSharesEventClock(detail);
  const charity = (detail.charity ?? []).map((c) => c.name).filter(Boolean).join(', ');

  return (
    <div className="mt-6 space-y-5">
      <BookingLinkDialog eventId={detail._id} current={booking} open={linking} onClose={() => setLinking(false)} />
      {/* The event's facts. */}
      <div data-guide="event-details"><Panel className="p-5">
        <SectionLabel>Event details</SectionLabel>
        <FactList
          columns={2}
          showEmpty
          facts={[
            {
              label: 'Client',
              value:
                detail.account?.name && detail.account.salesforceId ? (
                  <Link
                    to={`/accounts/${detail.account.salesforceId}`}
                    className="font-bold text-brand hover:underline"
                  >
                    {detail.account.name}
                  </Link>
                ) : (
                  detail.account?.name
                ),
            },
            { label: 'Contract', value: detail.contractNumber },
            {
              label: 'Portal request',
              // Which client booking this event delivers. Staff see it on
              // every event; the link is what keeps the client's My events
              // from showing a request and an event for the same thing.
              value:
                detail.bookingRequest === undefined ? undefined : (
                  <span className="flex flex-wrap items-center gap-2">
                    {booking ? (
                      <>
                        <span className="font-bold">{booking.reference}</span>
                        <span className="text-portal-muted">{booking.organizationName}</span>
                      </>
                    ) : (
                      <span className="text-portal-muted/70 italic">Not linked</span>
                    )}
                    <Button variant="secondary" size="compact" onClick={() => setLinking(true)}>
                      {booking ? 'Change' : 'Link'}
                    </Button>
                  </span>
                ),
            },
            {
              label: 'Stage',
              value: detail.stageName ? (
                <Pill tone={eventStageTone(detail.stageName)}>{detail.stageName}</Pill>
              ) : undefined,
            },
            {
              label: 'Program',
              // Content is contractor-visible; only the catalog list is not.
              value: detail.programId ? (
                <Link
                  to={`/programs/${detail.programId}`}
                  className="font-bold text-brand hover:underline"
                >
                  {detail.programDisplayName ?? detail.programName}
                </Link>
              ) : (
                (detail.programDisplayName ?? detail.programName)
              ),
            },
            {
              label: 'Activity',
              // "Not Applicable" reads as an absent value, not information.
              value: /not applicable/i.test(detail.activity ?? '') ? (
                <span className="text-portal-muted/70 italic">Not applicable</span>
              ) : (
                detail.activity
              ),
            },
            { label: 'Participants', value: participants },
            { label: 'Account type', value: detail.account?.type },
            { label: 'Charity', value: charity },
            {
              label: 'Date',
              value: detail.eventDate ? (
                <Link
                  // The day filter matches on the stored `eventDate`, so the
                  // link carries that field's own UTC day even though the
                  // label shows the event's true day.
                  to={`/places/events?onDate=${String(detail.eventDate).slice(0, 10)}`}
                  title="See everything on this date"
                  className="font-bold text-brand hover:underline"
                >
                  {formatEventDate(detail)}
                </Link>
              ) : undefined,
            },
            {
              label: 'Time',
              value: time ? (
                <>
                  {time}
                  {hasEventZone(detail) ? null : (
                    <span className="mt-0.5 block text-portal-micro font-semibold text-rose-600">
                      No timezone on this event — set one in Salesforce. Times are being read as
                      Eastern.
                    </span>
                  )}
                </>
              ) : undefined,
            },
            {
              label: 'Your local time',
              value: local ? (
                <>
                  <span className="block">
                    {local.date}, {local.time}
                  </span>
                  <span className="mt-0.5 block text-portal-micro text-portal-muted">
                    {sameClock ? `Same clock as the venue · ${local.zone}` : local.zone}
                  </span>
                </>
              ) : undefined,
            },
            { label: 'Opportunity', value: detail.opportunityName },
          ]}
        />
      </Panel></div>

      {/* Sibling sites and amendments booked under the same contract number,
          right under the details so a program manager sees the rest of the
          group before the contacts. Hidden when the event stands alone. */}
      <div data-guide="event-family" className="empty:hidden">
        <ContractFamilyPanel eventId={detail._id} />
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Everyone on the event who isn't BCE: the client's contacts, the
            venue contacts, and the charity's people — one card. */}
        <Panel className="p-5">
          <div data-guide="event-contacts" className="mb-3 flex items-center justify-between gap-2">
            <SectionLabel className="mb-0">Contacts</SectionLabel>
            {canEditContacts ? (
              <Button guide="event-add-contact" variant="secondary" size="compact" onClick={() => setAddingContact(true)}>
                <PlusIcon aria-hidden className="size-4" /> Add contact
              </Button>
            ) : null}
          </div>
          {/* Hairline separators between people — one card, clear breaks. */}
          <div className="divide-y divide-portal-line [&>*]:py-3.5 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
            {detail.contact && (detail.contact.name || detail.contact.email) ? (
              <ContactBlock label="Client · primary" source="Salesforce" contact={detail.contact} />
            ) : null}
            {(detail.contacts ?? [])
              .filter((contact) => !contact.isPrimary && (contact.name || contact.email))
              .map((contact) => (
                <ContactBlock
                  key={`${contact.name}-${contact.email}`}
                  label={contact.role ? `Client · ${contact.role.toLowerCase()}` : 'Client'}
                  source="Salesforce"
                  contact={contact}
                />
              ))}
            <LocalContacts
              eventId={detail._id}
              contacts={detail.localContacts ?? []}
              canEdit={canEditContacts}
              isAdmin={isAdmin}
              adding={addingContact}
              onAddingChange={setAddingContact}
            />
            <VenueContactBlock label="Venue contact" contact={detail.venueContact} />
            <VenueContactBlock label="Ending venue contact" contact={detail.endingVenueContact} />
            {(detail.charity ?? []).map((charity) =>
              charity.charityRep || charity.deliveryContact ? (
                <div key={charity.name ?? 'charity'}>
                  <FieldLabel>Charity{charity.name ? ` · ${charity.name}` : ''}</FieldLabel>
                  {charity.charityRep ? (
                    <p className="mt-0.5 text-portal-small text-portal-ink">
                      {charity.charityRep}
                      <span className="text-portal-muted"> — rep</span>
                    </p>
                  ) : null}
                  {charity.deliveryContact ? (
                    <p className="mt-0.5 text-portal-small text-portal-ink">
                      {charity.deliveryContact}
                      <span className="text-portal-muted"> — delivery</span>
                    </p>
                  ) : null}
                </div>
              ) : null,
            )}
          </div>
        </Panel>

        {/* The BCE team on this event. */}
        <Panel guide="event-team" className="p-5">
          <SectionLabel>BCE team</SectionLabel>
          <div className="space-y-3">
            <PersonRow role="Lead facilitator" person={detail.leadFacilitator} />
            <PersonRow role="Co-facilitator" person={detail.coFacilitator} />
            <PersonRow role="Sales contact" person={detail.salesperson} />
            <PersonRow role="Program manager" person={detail.programManager} />
            <PersonRow role="Contract manager" person={detail.contractManager} />
            <PersonRow role="Accounting" person={detail.accountingContact} />
            <PersonRow role="Production" person={detail.productionContact} />
          </div>
        </Panel>

        {/* Venue(s) — contacts live in the Contacts card. */}
        <Panel guide="event-venue" className="p-5">
          <SectionLabel>Venue</SectionLabel>
          <VenueBlock label="Starting" venue={detail.startVenue} />
          {detail.endVenue && detail.endVenue.name ? (
            <div className="mt-4 border-t border-portal-line pt-4">
              <VenueBlock label="Ending" venue={detail.endVenue} />
            </div>
          ) : null}
        </Panel>
      </div>

      <div data-guide="event-charity">{internalViewer ? <CharityPanel detail={detail} canEdit /> : null}</div>
      <div data-guide="event-recordings">
        <RecordingsPanel eventId={detail._id} recordings={detail.recordings ?? []} canEdit={internalViewer} />
      </div>
    </div>
  );
}

/** A small uppercase section eyebrow for the overview panels. */
function SectionLabel({ children, className }: { readonly children: React.ReactNode; readonly className?: string }): React.ReactElement {
  return (
    <h2 className={cn('mb-3 text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase', className)}>
      {children}
    </h2>
  );
}

/** One BCE team member row: avatar, name (linked to profile), role. Renders
 * nothing when the slot is empty, so absent roles just drop out. */
function PersonRow({
  role,
  person,
}: {
  readonly role: string;
  readonly person: EventPerson | string | null | undefined;
}): React.ReactElement | null {
  if (typeof person !== 'object' || person === null) return null;
  const name = personName(person);
  return (
    <div className="flex items-center gap-2.5">
      <Avatar name={name} size={28} imageUrl={avatarSrc(person) ?? undefined} />
      <span className="min-w-0">
        <Link
          to={`/people/${person._id}`}
          className="block truncate text-portal-small font-bold text-brand hover:underline"
        >
          {name}
        </Link>
        <span className="block text-portal-micro text-portal-muted">{role}</span>
      </span>
    </div>
  );
}

/** A labelled tel/mailto contact line, hidden when there is no value. */
function ContactLine({
  icon,
  label,
  value,
  kind,
}: {
  readonly icon: 'phone' | 'mail';
  readonly label: string;
  readonly value: string | undefined;
  readonly kind: 'tel' | 'mailto';
}): React.ReactElement | null {
  if (!value) return null;
  const href = kind === 'tel' ? `tel:${value.replace(/[^\d+]/g, '')}` : `mailto:${value}`;
  return (
    <a href={href} className="flex items-center gap-2 text-portal-small hover:underline">
      {icon === 'phone' ? (
        <PhoneIcon aria-hidden className="size-3.5 shrink-0 text-portal-muted" />
      ) : (
        <MailIcon aria-hidden className="size-3.5 shrink-0 text-portal-muted" />
      )}
      <span className="sr-only">{label}: </span>
      <span className="truncate text-brand-link">{value}</span>
    </a>
  );
}

/** A venue block: name, mapped address, room, and venue contact. */
/** The uppercase field label used across the overview cards (FactList's dt). */
function FieldLabel({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return (
    <p className="text-portal-micro font-extrabold tracking-[0.6px] text-portal-muted uppercase">
      {children}
    </p>
  );
}

/** One outside person in the Contacts card: label, name, reachable lines. */
function ContactBlock({
  label,
  source,
  contact,
}: {
  readonly label: string;
  /** Where the record comes from, when it is not ours to edit. */
  readonly source?: string;
  readonly contact: EventContact;
}): React.ReactElement {
  return (
    <div>
      <FieldLabel>
        {label}
        {source ? <span className="ml-1.5 font-semibold tracking-normal normal-case text-portal-muted/70">from {source}</span> : null}
      </FieldLabel>
      {/* Email is the directory identity — with one, the name opens the
          contact's page (their info + every connected event). */}
      {contact.email ? (
        <Link
          to={`/contacts/by-email/${encodeURIComponent(contact.email)}`}
          className="mt-1 block text-portal-small font-bold text-brand hover:underline"
        >
          {contact.name || contact.email}
        </Link>
      ) : (
        <p className="mt-1 text-portal-small font-bold text-portal-ink">{contact.name}</p>
      )}
      <div className="mt-1 space-y-1">
        <ContactLine icon="phone" label="Phone" value={contact.phone} kind="tel" />
        <ContactLine icon="phone" label="Mobile" value={contact.cell} kind="tel" />
        <ContactLine icon="mail" label="Email" value={contact.email} kind="mailto" />
      </div>
    </div>
  );
}

/** A venue contact in the Contacts card; renders nothing when absent. */
function VenueContactBlock({
  label,
  contact,
}: {
  readonly label: string;
  readonly contact: EventDetail['venueContact'];
}): React.ReactElement | null {
  const name = [contact?.firstName, contact?.lastName].filter(Boolean).join(' ');
  if (!contact || (!name && !contact.email && !contact.phone)) return null;
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      {name ? <p className="mt-1 text-portal-small font-bold text-portal-ink">{name}</p> : null}
      <div className="mt-1 space-y-1">
        <ContactLine icon="phone" label="Phone" value={contact.phone} kind="tel" />
        <ContactLine icon="mail" label="Email" value={contact.email} kind="mailto" />
      </div>
    </div>
  );
}

function VenueBlock({
  label,
  venue,
}: {
  readonly label: string;
  readonly venue: EventDetail['startVenue'];
}): React.ReactElement {
  if (!venue || !venue.name) {
    return <p className="text-portal-small text-portal-muted">No {label.toLowerCase()} venue set.</p>;
  }
  const address = [venue.street, venue.city, venue.state, venue.postalCode].filter(Boolean).join(', ');
  return (
    <div className="space-y-4">
      <div>
        <FieldLabel>{label}</FieldLabel>
        <Link
          to={`/places/events?venue=${encodeURIComponent(venue.name)}`}
          className="mt-1 block text-portal-small font-bold text-brand hover:underline"
        >
          {venue.name}
        </Link>
        {address ? (
          <a
            href={googleMapsUrl(address)}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-0.5 block text-portal-micro text-brand-link hover:underline"
          >
            {address}
          </a>
        ) : null}
      </div>
      {venue.room ? (
        <div>
          <FieldLabel>Room</FieldLabel>
          <p className="mt-1 text-portal-small text-portal-ink">{venue.room}</p>
        </div>
      ) : null}
    </div>
  );
}

/** A Google Maps search link for an address string. */
function googleMapsUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/**
 * The people on an event, and the controls to change them.
 *
 * Assignment is the whole point of this tab: search anyone in bcecloud —
 * internal facilitators or the client's own people — and put them on the
 * event. Internal staff, contract facilitators and client-side people are
 * grouped separately because "who from BCE is running this", "who is
 * delivering it" and "who from the client is attending" are different
 * questions.
 *
 * @param props - Event id and its current people.
 * @returns The people tab.
 */
function PeopleTab({
  eventId,
  people,
  loading,
  contacts,
  contactEmails,
}: {
  readonly eventId: string;
  readonly people: readonly EventPerson[];
  readonly loading: boolean;
  readonly contacts: readonly EventContact[];
  readonly contactEmails: readonly string[];
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState<RosterKind | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  // Managing the roster is internal-only; contractors read it. The API
  // enforces the same on add/remove and the people search.
  const identity = useQuery({
    queryKey: ['my-identity'],
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });
  const canManage = ['internal', 'admin', 'superadmin'].includes(identity.data?.role ?? '');

  /** Refreshes the roster after a change. */
  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['event-users', eventId] });
  }

  const add = useMutation({
    mutationFn: (userId: string) => addEventUsers(eventId, [userId]),
    onSuccess: async () => {
      setAdding(undefined);
      await refresh();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => removeEventUsers(eventId, [userId]),
    onSuccess: refresh,
    onError: (err) => setError(errorMessage(err)),
  });

  const assignedIds = new Set(people.map((person) => person._id));
  const internal = people.filter((person) =>
    ['internal', 'admin', 'superadmin'].includes(person.role ?? ''),
  );
  const facilitators = people.filter((person) => person.role === 'contractor');
  const clientSide = people.filter(
    (person) => !['internal', 'admin', 'superadmin', 'contractor'].includes(person.role ?? ''),
  );

  /** The section's Add button, for staff only. */
  function addButton(kind: RosterKind): React.ReactElement | undefined {
    if (!canManage) return undefined;
    return (
      <Button size="compact" onClick={() => setAdding(kind)}>
        <PlusIcon aria-hidden className="size-4" /> {ROSTER_KINDS[kind].action}
      </Button>
    );
  }
  const manage = canManage ? { onRemove: (id: string) => remove.mutate(id) } : {};

  return (
    <div className="space-y-5">
      {error === undefined ? null : (
        <ErrorNotice inline title="Could not update people" message={error} />
      )}

      <RosterGroup
        title="BCE team"
        hint="Internal staff running the event."
        emptyTitle="No BCE staff yet"
        people={internal}
        loading={loading}
        action={addButton(ROSTER_KIND.team)}
        {...manage}
      />
      <RosterGroup
        title="Facilitators"
        hint="Contract facilitators delivering the event."
        emptyTitle="No facilitators yet"
        people={facilitators}
        loading={loading}
        action={addButton(ROSTER_KIND.facilitator)}
        {...manage}
      />

      <ClientSection
        eventId={eventId}
        people={clientSide}
        contacts={contacts}
        contactEmails={contactEmails}
        loading={loading}
        action={addButton(ROSTER_KIND.client)}
        {...manage}
      />

      <AssistantsPanel eventId={eventId} isInternal={canManage} />

      {adding === undefined ? null : (
        <AddEventPersonDialog
          kind={adding}
          assignedIds={assignedIds}
          adding={add.isPending}
          onAdd={(userId) => {
            setError(undefined);
            add.mutate(userId);
          }}
          onClose={() => setAdding(undefined)}
        />
      )}
    </div>
  );
}

/**
 * One group of the roster.
 *
 * @param props - Title, hint and the people in the group.
 * @returns The group panel.
 */
function RosterGroup({
  title,
  hint,
  emptyTitle,
  people,
  loading,
  action,
  onRemove,
}: {
  readonly title: string;
  readonly hint: string;
  /** "No facilitators yet": the empty state's heading. */
  readonly emptyTitle: string;
  readonly people: readonly EventPerson[];
  readonly loading: boolean;
  /** The section's Add button; absent for read-only viewers. Repeated in the empty state. */
  readonly action?: React.ReactNode;
  /** Absent → read-only: no Remove control (contractors view, staff manage). */
  readonly onRemove?: (userId: string) => void;
}): React.ReactElement {
  return (
    <Panel>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-portal-line px-4 py-3">
        <div>
          <h2 className="text-portal-card-title font-bold">{title}</h2>
          <p className="text-portal-micro text-portal-muted">{hint}</p>
        </div>
        {action}
      </div>
      {loading ? (
        <LoadingState label={`Loading ${title.toLowerCase()}…`} />
      ) : people.length === 0 ? (
        <EmptyState title={emptyTitle} message={hint} {...(action ? { action } : {})} />
      ) : (
        <ul>
          {people.map((person) => (
            <li
              key={person._id}
              className="flex items-center gap-3 border-b border-portal-line px-4 py-2.5 last:border-b-0"
            >
              <Avatar name={personName(person)} size={32} />
              <span className="min-w-0 flex-1">
                <b className="block truncate text-portal-small">{personName(person)}</b>
                <span className="block truncate text-portal-micro text-portal-muted">
                  {person.email}
                </span>
              </span>
              {person.role ? (
                <Pill tone={person.role === 'client' ? 'brand' : 'neutral'}>{person.role}</Pill>
              ) : null}
              {onRemove ? (
                <button
                  type="button"
                  onClick={() => onRemove(person._id)}
                  className="text-portal-micro text-portal-muted hover:text-rose-700"
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

